import { Box, Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import ClearIcon from "@mui/icons-material/Clear";
import { blue, divider, black } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";

export const TalentFormWorkExperience = () => {
  const { control } = useFormContext<TTalentForm>();
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "workExperiences",
  });

  const workExperiences = useWatch({ control, name: "workExperiences" }) || [];

  const hasIncompleteWorkExperience = useMemo(() => {
    if (!workExperiences || workExperiences.length === 0) return true;

    return workExperiences.some((exp) => !exp.jobType?.trim() && !exp.companyName?.trim() && !exp.startDate && !exp.endDate);
  }, [workExperiences]);

  useEffect(() => {
    if (fields.length === 0) {
      append({
        jobType: "",
        companyName: "",
        startDate: null,
        endDate: null,
      });
    }
  }, [fields.length, append]);

  const handleAddField = () => {
    if (fields.length < 10 && !hasIncompleteWorkExperience) {
      append({
        jobType: "",
        companyName: "",
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleRemoveField = (index: number) => {
    if (fields.length > 1) {
      const experience = workExperiences[index];
      if (experience && !experience.jobType?.trim() && !experience.companyName?.trim() && !experience.startDate && !experience.endDate) {
        remove(index);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && fields.length > 1) {
      remove(deleteIndex);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  return (
    <>
      <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box flex={1}>
            <Typography variant="head20Bold" color={black[30]}>
              職歴
            </Typography>
          </Box>
        </Stack>

        {fields.map((field, index) => (
          <InnerBox key={field.id} mt={3} p={2} pt={0} showDivider={false} borderColor={divider.high} bordered={true}>
            {fields.length > 1 && (
              <Stack mt={2} mb={1}>
                <Typography variant="sub16Bold" color={black[30]}>
                  職歴{index + 1}
                </Typography>
              </Stack>
            )}

            <Stack mt={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormField control={control} name={`workExperiences.${index}.jobType`} label="職種">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>

            <Stack mt={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormField control={control} name={`workExperiences.${index}.companyName`} label="企業名">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>

            <Stack mt={2}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField control={control} name={`workExperiences.${index}.startDate`} label="入社年月">
                    <InputDatePicker placeholder="1990/06/25" minDate={dayjs("1900-01-01")} maxDate={dayjs()} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name={`workExperiences.${index}.endDate`} label="退職年月">
                    <InputDatePicker placeholder="1990/06/25" minDate={dayjs("1900-01-01")} maxDate={dayjs()} />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>

            {fields.length > 1 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer", mt: 1 }} onClick={() => handleRemoveField(index)}>
                <ClearIcon sx={{ color: blue[80], fontSize: 18 }} />
                <Typography variant="cap12Light">削除</Typography>
              </Stack>
            )}
          </InnerBox>
        ))}

        {fields.length < 10 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            mt={1}
            sx={{
              cursor: hasIncompleteWorkExperience ? "not-allowed" : "pointer",
              opacity: hasIncompleteWorkExperience ? 0.5 : 1,
            }}
            onClick={handleAddField}
          >
            <AddCircleOutlineIcon
              sx={{
                color: hasIncompleteWorkExperience ? black[60] : blue[80],
                fontSize: 18,
              }}
            />
            <Typography variant="cap12">職歴を追加</Typography>
          </Stack>
        )}
      </InnerBox>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「職歴」を削除してもよろしいですか？"
      />
    </>
  );
};
